<template>
    <div class="assign-driver-page">
        <ts-page-title
            :title="$t('assignDriver.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('assignDriver.pageTitle'),
                    href: '/admin/agency/driver-assigns'
                },
                {
                    text: $t('create'),
                    active: true
                }
            ]"
        />

        <ts-loading-banner :loading="loading">
            <div class="assign-page-content">
                <DriverSetupSection
                :driver-id="model.driver_id"
                :driver-list="driverList"
                :errors="errors"
                @change-driver="onDriverChange"
                @search-driver="handleDriverSearch"
            />

                <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-4">
                <AvailablePackagesPanel
                    ref="availablePackagesPanel"
                    :rows="availableRows"
                    :selected-ids="model.booking_list_id"
                    :search-box="search_box"
                    :receiver-phone="receiver_phone_search"
                    :zone-id="zone_id"
                    :zone-list="zoneList"
                    @update-search-box="search_box = $event"
                    @update-receiver-phone="receiver_phone_search = $event"
                    @search-packages="searchPackages"
                    @search-receiver-phone="searchReceiverPhone"
                    @search-zone="handleZoneSearch"
                    @change-zone="onZoneChange"
                    @clear-filters="clearFilters"
                    @select-all="selectAllMatching"
                    @add="addItem"
                />

                <SelectedPackagesPanel
                    :records="selectedBooking"
                    :total-packages="totalSelectedPackages"
                    :errors="errors"
                    @clear="clearSelection"
                    @delete="deleteRecord"
                />
                </div>
            </div>
        </ts-loading-banner>

        <AssignActionFooter
            :selected-driver-name="selectedDriverName"
            :selected-count="selectedBooking.length"
            :can-assign="canAssign"
            @cancel="onCancel"
            @clear="clearSelection"
            @save="onSave"
        />

        <a-modal
            :title="$t('pdf')"
            width="50%"
            v-model="preview_pdf"
            :footer="null"
        >
            <ts-preview-pdf v-model="waiting" :src="src" />
        </a-modal>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import { debounce } from "lodash";
import { Errors } from "form-backend-validation";
import AssignActionFooter from "./components/AssignActionFooter.vue";
import AvailablePackagesPanel from "./components/AvailablePackagesPanel.vue";
import DriverSetupSection from "./components/DriverSetupSection.vue";
import SelectedPackagesPanel from "./components/SelectedPackagesPanel.vue";
export default {
    name: "assignDriverCreate",
    components: {
        AssignActionFooter,
        AvailablePackagesPanel,
        DriverSetupSection,
        SelectedPackagesPanel
    },
    data() {
        return {
            waiting: false,
            preview_pdf: false,
            src: "",
            errors: new Errors(),
            loading: false,
            search_box: null,
            zone_id: null,
            bookingList: [],
            selectedBooking: [],
            model: {
                driver_id: null,
                booking_list_id: []
            },
            receiver_phone_search: null,
            last_match_count: 0,
            driverList: [],
            zoneList: []
        };
    },
    computed: {
        selectedDriver() {
            return this.driverList.find(driver =>
                this.idsEqual(driver.driver_id, this.model.driver_id)
            );
        },
        selectedDriverName() {
            return this.selectedDriver
                ? this.selectedDriver.driver_name
                : this.$t("assignDriver.driver");
        },
        canAssign() {
            return !!this.model.driver_id && this.selectedBooking.length > 0;
        },
        availableRows() {
            return this.bookingList.filter(record => !this.isSelected(record));
        },
        totalSelectedPackages() {
            return this.selectedBooking.reduce((total, record) => {
                return total + Number(record.number_of_package || 0);
            }, 0);
        }
    },
    methods: {
        ...mapActions("agency/assignDriver", [
            "store",
            "searchBooking",
            "getFormViewData"
        ]),
        searchPackages() {
            this.onSearchInput({ focusFirstPackage: true });
        },
        focusReceiverPhoneInput(selectText = false) {
            this.$nextTick(() => {
                const panel = this.$refs.availablePackagesPanel;

                if (panel && panel.focusReceiverPhoneInput) {
                    panel.focusReceiverPhoneInput(selectText);
                }
            });
        },
        loadAvailablePackages() {
            this.getBooking({ limit: 300 });
        },
        searchReceiverPhone() {
            this.onSearchInput({ focusFirstPackage: true });
        },
        clearFilters() {
            this.search_box = null;
            this.receiver_phone_search = null;
            this.zone_id = null;
            this.bookingList = [];
            this.last_match_count = 0;
        },
        clearSelection() {
            this.selectedBooking = [];
            this.model.booking_list_id = [];
        },
        normalizeId(value) {
            return value === undefined || value === "" ? null : value;
        },
        idsEqual(left, right) {
            return String(left) === String(right);
        },
        onDriverChange(value) {
            this.model.driver_id = this.normalizeId(value);
        },
        mergeDriverOptions(options) {
            const drivers = Array.isArray(options) ? options : [];
            const driverId = this.normalizeId(this.model.driver_id);

            if (driverId === null) {
                return drivers;
            }

            const selectedDriver = this.driverList.find(driver =>
                this.idsEqual(driver.driver_id, driverId)
            );
            const hasSelectedDriver = drivers.some(driver =>
                this.idsEqual(driver.driver_id, driverId)
            );

            if (selectedDriver && !hasSelectedDriver) {
                return [selectedDriver, ...drivers];
            }

            return drivers;
        },
        isSelected(record) {
            return this.model.booking_list_id.some(id => id == record.booking_id);
        },
        selectAllMatching() {
            this.availableRows.forEach(record => this.addItem(record));
        },
        handleDriverSearch: debounce(async function(value) {
            this.loading = true;

            try {
                let rep = await this.getFormViewData({
                    index: "driver",
                    params: {
                        fnName: "driver",
                        search_driver: value,
                        selected_driver_id: this.model.driver_id
                    }
                });
                this.driverList = this.mergeDriverOptions(rep.data["driver"] || []);
            } catch (error) {
                this.$notify({ type: "error", text: error.message });
            } finally {
                this.loading = false;
            }
        }, 1000),
        handleZoneSearch: debounce(async function(value) {
            if (!value) return false;
            this.loading = true;

            try {
                var rep = await this.getFormViewData({
                    index: "zone",
                    params: {
                        fnName: "zone",
                        search_zone: value
                    }
                });
                this.zoneList = rep.data["zone"] || [];
            } catch (error) {
                this.$notify({ type: "error", text: error.message });
            } finally {
                this.loading = false;
            }
        }, 1000),
        onZoneChange(value) {
            this.zone_id = value || null;
        },
        onSearchInput(options = {}) {
            this.getBooking(
                {
                    zone_id: this.zone_id,
                    search_booking: this.search_box,
                    receiver_phone: this.receiver_phone_search,
                    limit: 300
                },
                options
            );
        },
        focusFirstPackageRow() {
            this.$nextTick(() => {
                const panel = this.$refs.availablePackagesPanel;

                if (panel && panel.focusFirstPackageRow) {
                    panel.focusFirstPackageRow();
                }
            });
        },
        getBooking(value, options = {}) {
            this.loading = true;
            this.searchBooking(value)
                .then(response => {
                    this.bookingList = response.data || [];
                    this.last_match_count = this.bookingList.length;

                    if (options.focusFirstPackage && this.availableRows.length) {
                        this.focusFirstPackageRow();
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        async fetchFormOptions() {
            this.loading = true;

            try {
                let rep = await this.getFormViewData({
                    index: "driver,zone",
                    params: {
                        fnName: "driver,zone",
                        selected_driver_id: this.model.driver_id
                    }
                });
                this.driverList = this.mergeDriverOptions(rep.data["driver"] || []);
                this.zoneList = rep.data["zone"] || [];
            } catch (error) {
                this.$notify({ type: "error", text: error.message });
            } finally {
                this.loading = false;
            }
        },
        addItem(item) {
            var existing = this.model.booking_list_id.find(
                id => id == item.booking_id
            );
            if (!existing) {
                this.selectedBooking.push(item);
                this.model.booking_list_id.push(item.booking_id);
            }

            this.focusReceiverPhoneInput(true);
        },
        deleteRecord(record) {
            this.selectedBooking.splice(
                this.selectedBooking.indexOf(
                    this.selectedBooking.find(
                        b => b.booking_id == record.booking_id
                    )
                ),
                1
            );

            this.model.booking_list_id.splice(
                this.model.booking_list_id.indexOf(record.booking_id),
                1
            );
        },
        onCancel() {
            this.removeKeyData();
            this.$router.push("/admin/agency/driver-assigns");
        },
        onSave() {
            this.src = "";
            this.errors = new Errors();
            this.waiting = true;
            this.preview_pdf = true;
            this.store(this.model)
                .then(response => {
                    this.$notify({ type: "success", text: response.message });
                    this.clearInput();
                    this.src = response.url;
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.$notify({ type: "error", text: error.message });
                    this.preview_pdf = false;
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        clearInput() {
            this.bookingList = [];
            this.selectedBooking = [];
            this.model.driver_id = null;
            this.model.booking_list_id = [];
            this.removeKeyData();
        },
        setValueModel() {
            const cachedModel = localStorage.getItem("model_assign");

            if (!cachedModel) {
                return;
            }

            try {
                const data = JSON.parse(cachedModel);
                const model = data.model || {};

                this.bookingList = Array.isArray(data.bookingList)
                    ? data.bookingList
                    : [];
                this.selectedBooking = Array.isArray(data.selectedBooking)
                    ? data.selectedBooking
                    : [];
                this.model = {
                    driver_id: this.normalizeId(model.driver_id),
                    booking_list_id: Array.isArray(model.booking_list_id)
                        ? model.booking_list_id
                        : []
                };
                this.receiver_phone_search = data.receiver_phone_search || null;
                this.last_match_count = data.last_match_count || 0;
                this.driverList = Array.isArray(data.driverList)
                    ? data.driverList
                    : [];
                this.zoneList = Array.isArray(data.zoneList) ? data.zoneList : [];
            } catch (error) {
                this.removeKeyData();
            }
        },
        removeKeyData() {
            localStorage.removeItem("model_assign");
        }
    },
    mounted() {
        document.body.classList.add("assign-driver-body-lock");
    },
    beforeDestroy() {
        document.body.classList.remove("assign-driver-body-lock");
    },
    watch: {
        model: {
            handler() {
                localStorage.setItem(
                    "model_assign",
                    JSON.stringify({
                        model: this.model,
                        selectedBooking: this.selectedBooking,
                        bookingList: this.bookingList,
                        receiver_phone_search: this.receiver_phone_search,
                        last_match_count: this.last_match_count,
                        driverList: this.driverList,
                        zoneList: this.zoneList
                    })
                );
            },
            deep: true
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.setValueModel();
            vm.fetchFormOptions();
            vm.loadAvailablePackages();
            vm.focusReceiverPhoneInput();
        });
    }
};
</script>

<style>
.assign-driver-body-lock {
    overflow: hidden;
}

.assign-driver-page {
    height: calc(100vh - 50px);
    overflow: hidden;
}

.assign-page-content {
    padding-bottom: 92px;
}

.assign-input-enter {
    align-items: center;
    background: #f3f6fb;
    border: 1px solid #dbe3ef;
    border-radius: 3px;
    color: #7a8ba6;
    display: inline-flex;
    font-size: 10px;
    font-weight: 500;
    height: 18px;
    padding: 0 6px;
}

.assign-package-panel {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 360px);
    min-height: 420px;
    overflow: hidden;
}

.assign-package-row {
    min-height: 62px;
}

.assign-info-chip {
    align-items: center;
    background: #f3f6fb;
    border-radius: 4px;
    color: #64748b;
    display: inline-flex;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    min-height: 20px;
    padding: 3px 8px;
    white-space: nowrap;
}

.available-package-scroll,
.selected-package-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.selected-package-empty {
    min-height: 100%;
}

.assign-page-footer {
    bottom: 0;
    min-height: 56px;
    left: 220px;
    position: fixed;
    right: 0;
    z-index: 1020;
}

.app-sidebar-minified .assign-page-footer {
    left: 60px;
}

.app-without-sidebar .assign-page-footer,
.app-sidebar-mobile-toggled .assign-page-footer {
    left: 0;
}

@media (max-height: 760px) {
    .assign-package-panel {
        height: calc(100vh - 310px);
        min-height: 320px;
    }
}

@media (max-width: 991.98px) {
    .assign-driver-page {
        height: auto;
        overflow: visible;
    }

    .assign-page-content {
        padding-bottom: 132px;
    }

    .assign-package-panel {
        height: auto;
        min-height: 0;
    }

    .available-package-scroll,
    .selected-package-scroll {
        max-height: 420px;
    }

    .assign-page-footer {
        align-items: stretch;
        flex-direction: column;
        left: 0;
    }
}
</style>
